import {AnimatePresence, motion, useDragControls, useIsPresent, useReducedMotion} from "motion/react";
import {createContext, Fragment, type CSSProperties, type ReactNode, useContext, useEffect, useRef} from "react";

import {FLING_VELOCITY, projectEndpoint, SETTLE_SPRING} from "../../../utils/gesture";
import {ModalCloseButton} from "../core/button";
import {Portal} from "./Portal";
import {ScrollArea} from "./ScrollArea";

const ASPECT_W = 9;
const ASPECT_H = 16;
const FRAME_IDEAL_WIDTH_PX = 400;
/**
 * Единая длительность появления/исчезновения рамки модалки — то же значение,
 * что у ConfirmDialog (bounce: 0, ~0.3s): без отскока и без «затянутой»
 * пружины по умолчанию (её bounce давал перелёт и ~450ms на закрытие —
 * жалоба продакт-оунера «окна хлопают»). Экспортируется, чтобы подложки,
 * которые должны исчезать СИНХРОННО с рамкой (ModalHost, стек AppLayout),
 * не разъезжались с ней по времени на отдельных magic-number.
 */
export const MODAL_PANEL_TRANSITION_DURATION = 0.32;
const MODAL_PANEL_SPRING = {type: "spring", bounce: 0, duration: MODAL_PANEL_TRANSITION_DURATION} as const;
const MODAL_PANEL_REDUCED_MOTION_TRANSITION = {duration: 0.15} as const;
/** Длительность появления/исчезновения собственной (несовместной) подложки модалки. */
const OWN_BACKDROP_DURATION = 0.18;
/** Доп. классы рамки по умолчанию — модалка дописывает свои поверх базовых. */
const DEFAULT_CLASS_NAME = "";
const BORDER_RADIUS = "4xl";
/**
 * Скругления рамки перечислены целиком, а не собираются из кусков: Tailwind ищет
 * имена классов по исходникам как текст, и `sm:rounded-${x}` в сборку не попадёт —
 * у модалки просто пропали бы скругления на десктопе.
 */
const BORDER_RADIUS_CLASSES: Record<string, {bottomSheet: string; topSheet: string; centered: string}> = {
    "2xl": {
        bottomSheet: "rounded-t-2xl sm:rounded-t-2xl",
        topSheet: "rounded-b-2xl sm:rounded-b-2xl",
        centered: "rounded-2xl sm:rounded-2xl",
    },
    "3xl": {
        bottomSheet: "rounded-t-3xl sm:rounded-t-3xl",
        topSheet: "rounded-b-3xl sm:rounded-b-3xl",
        centered: "rounded-3xl sm:rounded-3xl",
    },
    "4xl": {
        bottomSheet: "rounded-t-4xl sm:rounded-t-4xl",
        topSheet: "rounded-b-4xl sm:rounded-b-4xl",
        centered: "rounded-4xl sm:rounded-4xl",
    },
};

/* ── Смахивание нижней шторки вниз (27.08, п. 18 Ирины: «выезжает правильно, но
      смахнуть вниз для закрытия нельзя, есть только крестик») ─────────────────
   Порог и физика взяты из общего app/utils/gesture.ts, чтобы шторка закрывалась
   так же, как листается лента анкет и обучение: одно и то же движение пальца
   должно давать один и тот же результат по всему приложению. */

/** Смахивание считается доведённым, если проекция броска уходит ниже этой доли
 *  высоты шторки. Доля, а не пикселы: у низкой шторки 120 px — это «почти всё
 *  окно», у высокой — едва заметный сдвиг. */
const SHEET_DISMISS_FRACTION = 0.28;
/** Пол и потолок доли: у очень низких шторок жест не должен срабатывать от
 *  случайного сдвига, у очень высоких — требовать протащить полэкрана. */
const SHEET_DISMISS_MIN_PX = 72;
const SHEET_DISMISS_MAX_PX = 160;
/**
 * Сопротивление вверх (apple-design §9): за верхний край шторка почти не идёт,
 * но и не встаёт стеной — 6% пути пальца читаются как «дальше некуда», а не как
 * «интерфейс завис». Вниз — 1:1 с пальцем, без всякого замедления (§2).
 */
const SHEET_DRAG_ELASTIC = {top: 0.06, bottom: 1, left: 0, right: 0} as const;
/** Смещение, после которого отпускание пальца над кнопкой перестаёт быть кликом.
 *  Тот же порог, что у ленты анкет — ProfileCard.CLICK_SUPPRESS_OFFSET. */
const SHEET_CLICK_SUPPRESS_OFFSET = 6;

const SharedModalBackdropContext = createContext(false);
const ModalLayerContext = createContext<{zIndex?: number; interactive: boolean}>({
    interactive: true,
});

type ModalPosition = "default" | "center" | "top" | "bottom" | "topRight";

interface ModalShellProps {
    children: ReactNode;
    onClose?: () => void;
    position?: ModalPosition;
    disableBackdropClose?: boolean;
    hideCloseButton?: boolean;
    /** Доп. классы крестика закрытия — например text-red-500 для модалок с белым фоном без шапки. */
    closeButtonClassName?: string;
    /** Потолок высоты рамки (для center-позиции). Не задан — текущее поведение (92–100dvh).
     *  Нужен, чтобы окно не уезжало под плавающую пилюлю нижней навигации. */
    maxHeight?: string;
    /** Резервит снизу рамки зону под плавающую пилюлю нижней навигации + зазор.
     *  Задаётся в calc (напр. `calc(80px + env(safe-area-inset-bottom) + 6px)`), чтобы
     *  низ окна просмотра вставал ровно над пилюлей. */
    bottomInset?: string;
    /**
     * Слот поверх затемнённой подложки, ВНЕ белой рамки модалки (у рамки
     * overflow:hidden, поэтому изнутри наружу не выйти). Нужен листам, где
     * закрытие по референсу живёт на фоне, а не внутри листа: QR-код.
     */
    backdropSlot?: ReactNode;
    fitContent?: boolean;
    width?: number;
    className?: string;
    ariaLabel?: string;
    borderRadius?: string;
    /**
     * Шторку можно смахнуть вниз, а не только закрыть крестиком (п. 18 Ирины).
     *
     * Только для position="bottom" и только по явной просьбе модалки: жест
     * забирает вертикальный touch-action у всего листа, поэтому включать его у
     * шторки с прокруткой внутри нельзя — прокрутка перестала бы работать.
     * Первый и пока единственный потребитель — QRShareModal (лист по содержимому,
     * без скролла).
     */
    swipeToDismiss?: boolean;
    /**
     * #760: явный z-index слоя контейнера. По умолчанию слой берётся из
     * ModalLayerContext (стек dialogs: 80 + index*2), а без провайдера контейнер
     * держит класс z-[80] — равный z первой стековой модалки, поэтому порядок
     * решался порядком порталов в DOM. Вне-стековые модалки (MatchModal после
     * свайпа) задают zLayer ниже стека, и любая модалка воронки анализа
     * (compatAnalysis → analysisPurchase → analysisDetails) гарантированно выше
     * мэтч-карточки независимо от порядка монтирования порталов.
     */
    zLayer?: number;
    /**
     * Полноэкранная страница вместо плавающей карточки: без подложки (нет
     * контента за модалкой, который она бы затемняла), без скруглений/тени,
     * рамка занимает всю доступную область (100% × 100% контейнера, safe-area
     * по-прежнему учтена паддингом контейнера — см. `style` ниже).
     *
     * Первый и пока единственный потребитель — AuthModal в режиме `entryMode`
     * (Capacitor-обёртка стартует прямо на экране логина, `/?auth=login`):
     * плавающая карточка над пустым фоном там читается как забагованный
     * попап, а не как первый экран приложения (apple-design: launch screen
     * должен выглядеть как часть приложения, не как оверлей поверх него).
     * На обычном сайте (человек кликает «Войти» поверх лендинга) флаг не
     * передаётся — поведение не меняется.
     */
    fullPage?: boolean;
    /**
     * Рамка точно над карточкой ленты свайпов (Ирина, 01.10): те же x, y,
     * ширина, высота и радиус — из токенов `--rla-card-*` (app.css), которые
     * делит и `.rla-swipe-card-box`. Содержимое, которому не хватает места,
     * прокручивается внутри рамки, сама рамка не сжимается.
     * Только для центрированной позиции; размеры `width`/`maxHeight`/`fitContent`
     * при этом игнорируются.
     * По умолчанию ВКЛЮЧЕНО для любой центрированной «карточной» модалки (не
     * fitContent, не шторка, не fullPage): так подписка, верификация, магазины,
     * разбор и профильные экраны не «плавают» меньше/ниже карточки ленты.
     * `cardBox={false}` — явный отказ; `fitContent` (компактные диалоги по
     * содержимому) по умолчанию остаются как были.
     */
    cardBox?: boolean;
    /**
     * Экран-страница поверх приложения (issue #1066, Ирина: «совместимость и
     * подробный ИИ-разбор — страницами, а не карточками»). Геометрия та же, что
     * у `fullPage` (рамка на весь экран, safe-area внутри рамки, без подложки и
     * скругления), но появление — как у смены вкладок (#1055): прозрачность +
     * сдвиг 8 px, без «влёта» карточки. Reduce Motion — только прозрачность.
     */
    page?: boolean;
    /**
     * Своё затемнение даже внутри общего стека. Нужно окну, которое открывается
     * поверх страницы разбора (z-300): общее затемнение стека лежит ниже страницы
     * и её не гасит — без своего фона страница «просвечивает» под шторкой (#1066).
     */
    ownBackdrop?: boolean;
    /**
     * Только с `page`: рамка не добавляет отступ safe-area сверху — его держит
     * сама страница (страница разбора начинается с шапки приложения, у которой
     * свой `safe-top`, #1066). Иначе отступ удвоился бы.
     */
    bleedTop?: boolean;
}

/** Появление страницы — те же 8 px и та же кривая, что у смены вкладок (app.css). */
const PAGE_ENTER_OFFSET_PX = 8;
const PAGE_TRANSITION = {duration: 0.24, ease: [0.23, 1, 0.32, 1]} as const;

/**
 * #743 (iOS WebKit): тап по .rla-compat на карточке ленты открывал пейволл-окно
 * ЗА карточкой. Статический z-порядок корректен на всех движках (модалки
 * порталируются в body, 80+ > main z-10) — но на iPhone Safari/WebKit
 * композитор рисует композитные слои КАРТОЧКИ (backdrop-filter на .rla-compat,
 * tap-анимация `:active {transform: scale(.985)}`, motion-трансформы стека
 * карточек) ВЫШЕ fixed-портала модалки, если модальный слой сам не вынесен в
 * композитный слой. `translateZ(0)` на модальных слоях заставляет композитора
 * упорядочивать их по z-index (известный класс WebKit-багов с backdrop-filter;
 * «дёргает карточку» в жалобе — тот самый :active-скалер в момент тапа).
 * Не влияет на chromium/Firefox и не ломает свайп-жесты (#737) и анимации (#736):
 * transform ставится на КОНТЕЙНЕР модалки (fixed inset-0), не на анимируемую рамку.
 */
export const COMPOSITING_LAYER_FIX = "translateZ(0)" as const;

interface ModalHostProps {
    isOpen: boolean;
    children: ReactNode;
}

export function ModalHost({isOpen, children}: ModalHostProps) {
    return (
        <>
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        key="modal-backdrop"
                        initial={{opacity: 0}}
                        animate={{opacity: 1}}
                        exit={{opacity: 0}}
                        /*
                         * Раньше здесь стояло 0.18s — подложка гасла быстрее, чем рамка
                         * успевала доиграть exit (0.45s у пружины по умолчанию, потом
                         * 0.32s после фикса #1), и на закрытии модалка на миг оставалась
                         * висеть уже без затемнения под ней. Общая константа держит их
                         * СИНХРОННО.
                         */
                        transition={{duration: MODAL_PANEL_TRANSITION_DURATION}}
                        className="pointer-events-none fixed inset-0 z-[79] bg-black/50 backdrop-blur-sm"
                        /* #743 iOS WebKit: см. COMPOSITING_LAYER_FIX выше. */
                        style={{transform: COMPOSITING_LAYER_FIX}}
                    />
                )}
            </AnimatePresence>
            <SharedModalBackdropContext.Provider value>
                {children}
            </SharedModalBackdropContext.Provider>
        </>
    );
}

export function ModalLayerProvider({
    zIndex,
    interactive = true,
    children,
}: {
    zIndex?: number;
    interactive?: boolean;
    children: ReactNode;
}) {
    return (
        <ModalLayerContext.Provider value={{zIndex, interactive}}>
            {children}
        </ModalLayerContext.Provider>
    );
}

export function ModalShell({
    onClose,
    position = "center",
    disableBackdropClose = false,
    hideCloseButton = false,
    closeButtonClassName = "",
    backdropSlot,
    fitContent = false,
    width = FRAME_IDEAL_WIDTH_PX,
    className = DEFAULT_CLASS_NAME,
    borderRadius = BORDER_RADIUS,
    swipeToDismiss = false,
    zLayer,
    maxHeight,
    bottomInset,
    children,
    ariaLabel,
    fullPage: fullPageProp = false,
    cardBox,
    page = false,
    ownBackdrop = false,
    bleedTop = false,
}: ModalShellProps) {
    // Страница — это fullPage по геометрии; отличается только появлением.
    const fullPage = fullPageProp || page;
    const reduceMotion = useReducedMotion();
    const pressStartedOnBackdropRef = useRef(false);
    const frameRef = useRef<HTMLDivElement | null>(null);
    /** true — только что смахивали: ближайший click гасим (палец отпущен над кнопкой). */
    const suppressClickRef = useRef(false);
    const dragControls = useDragControls();
    const hasSharedBackdrop = useContext(SharedModalBackdropContext);
    const modalLayer = useContext(ModalLayerContext);
    // Модалка в процессе выхода не должна ловить клики. Подстраховка от «призраков»:
    // если AnimatePresence по какой-то причине не удалит узел, экран всё равно
    // останется рабочим, а не заблокируется прозрачной подложкой на весь вьюпорт.
    const isPresent = useIsPresent();
    const radiusClasses = BORDER_RADIUS_CLASSES[borderRadius] ?? BORDER_RADIUS_CLASSES[BORDER_RADIUS];
    const resolvedPosition = position === "default" ? "center" : position;
    const isBottom = resolvedPosition === "bottom";
    const isTop = resolvedPosition === "top";
    const isTopRight = resolvedPosition === "topRight";
    const canSwipeDismiss = swipeToDismiss && isBottom && Boolean(onClose);
    /* topRight (окно «Уведомления») тоже в коробке карточки: иначе при стеке
     * уведомления → разбор → детальный анализ его рамка (top 12px) торчала над
     * верхним окном (top 75px) — Ирина 01.10, реальный Android. Контейнер при этом
     * остаётся pointer-events-none (тапы по странице вне рамки проходят). */
    const useCardBox = (cardBox ?? !fitContent) && !fullPage && !isBottom && !isTop;

    useEffect(() => {
        if (!onClose) return;
        const handler = (event: KeyboardEvent) => {
            if (event.key === "Escape") onClose();
        };
        window.addEventListener("keydown", handler);
        return () => window.removeEventListener("keydown", handler);
    }, [onClose]);

    const frameStyle: CSSProperties = fullPage
        ? {
            /* Рамка = ВЕСЬ контейнер (fixed inset-0), без вычета safe-area —
             * иначе на реальном устройстве с ненулевым home-indicator/жестовой
             * зоной (env(safe-area-inset-bottom) > 0, в отличие от эмулятора,
             * где он часто 0 и баг не виден) контейнер оставался БЕЗ фона в
             * этой полосе: fullPage не рендерит ни общую подложку (см.
             * hasSharedBackdrop ниже), ни свою (см. `!fullPage` в условии
             * собственной подложки) — сквозь прозрачную полосу снизу
             * проглядывал контент СТРАНИЦЫ ПОД МОДАЛКОЙ (у входа это лендинг:
             * статичная демо-карточка «Анна, 28» из HeroSection, не реальная
             * лента — это не утечка состояния сессии, просто лендинг всегда
             * смонтирован под AuthModal). Скриншот Ирины 29.09, физическое
             * устройство. Safe-area теперь учитывается ВНУТРИ рамки, как
             * padding контента (ниже), а не как паддинг контейнера — фон
             * рамки (bg-white) достаёт до самого края экрана.
             */
            width: "100%",
            height: "100%",
            maxHeight: "100%",
            paddingTop: page && bleedTop ? 0 : "env(safe-area-inset-top)",
            paddingBottom: "env(safe-area-inset-bottom)",
            paddingLeft: "env(safe-area-inset-left)",
            paddingRight: "env(safe-area-inset-right)",
        }
        : useCardBox
        ? {
            width: "100%",
            height: "100%",
            maxHeight: "var(--rla-card-max-h)",
            borderRadius: "var(--rla-card-radius)",
        }
        : fitContent
        ? {
            /*
             * Размеры считаются ОТ ПОДЛОЖКИ (100%), а не от вьюпорта (100dvh/100vw).
             *
             * Раньше здесь стояло `calc(100dvh - 1.5rem)` — «вьюпорт минус те самые
             * 0.75rem паддинга сверху и снизу, которые подложка добавляет ниже».
             * Арифметика сходилась только на устройстве без выреза. На iPhone
             * подложка кладёт `max(env(safe-area-inset-top), .75rem)` = 47–59px
             * сверху и `max(env(safe-area-inset-bottom), .75rem)` = 34px снизу,
             * а карточка продолжала считать себе высоту по 24px — то есть была
             * выше свободного места на 57–69px. Подложка отцентрована и стоит с
             * `overflow: hidden`, поэтому лишнее срезалось сверху и снизу
             * поровну: нижний край карточки вместе с главной кнопкой уходил за
             * границу экрана, и доскроллить до него было нельзя (обрезание, а
             * не прокрутка). Ровно это и было на снимке продакт-оунера 28.08:
             * «Отмена» видна, градиентная кнопка под ней разрезана краем.
             *
             * `100%` для flex-элемента считается от content-box подложки, из
             * которого её паддинги уже вычтены, — safe-area учитывается ровно
             * один раз и при любом значении вырезa. Заодно уходит зависимость
             * от dvh, который в мобильном Safari меняется на лету вместе с
             * панелями браузера.
             */
            width: `min(${width}px, 100%)`,
            /*
             * fitContent + height:fit-content — рамка «auto» по высоте, и для
             * процентных размеров потомков (h-full) такой родитель не является
             * определённым: вложенный `flex flex-col h-full` резолвится в auto,
             * а `ScrollArea flex-1 min-h-0` не получает ограниченной высоты.
             * Контент растёт выше вьюпорта и срезается overflow-hidden рамки —
             * прокрутки нет (баг мобильного входа, #641).
             *
             * Когда модалка передаёт maxHeight (AuthModal), используем его и как
             * определённую ВЫСОТУ рамки — тогда h-full/flex-1 получают реальный
             * предел и контент скроллится. Без maxHeight поведение прежнее
             * (fit-content), короткие модалки не меняются.
             */
            maxHeight: maxHeight ?? "100%",
            height: maxHeight ?? "fit-content",
        }
        : isBottom
            ? {
                width: `min(${width}px, 100vw)`,
                height: "82dvh",
                maxHeight: "82dvh",
            }
            : isTop
                ? {
                    width: `min(${width}px, calc(100vw - 1.5rem))`,
                    height: "min(42rem, calc(100dvh - 5.25rem))",
                    maxHeight: "calc(100dvh - 5.25rem)",
                }
                : {
                    width: isTopRight
                        ? `min(${width}px, calc(100vw - 1.5rem), calc((100dvh - 5.25rem) * ${ASPECT_W} / ${ASPECT_H}))`
                        : `min(${width}px, calc(100vw - 1.5rem), calc((100dvh - 1.5rem) * ${ASPECT_W} / ${ASPECT_H}))`,
                    aspectRatio: `${ASPECT_W} / ${ASPECT_H}`,
                    maxHeight: maxHeight ?? (isTopRight ? "calc(100dvh - 5.25rem)" : "calc(100dvh - 1.5rem)"),
                };

    /* Reduce Motion (§10.3): никаких сдвигов/масштаба — только прозрачность,
     * тот же приём, что у ConfirmDialog (which reduces scale-in to a plain
     * opacity fade).
     *
     * fullPage — та же лестница, что и reduceMotion, но по другой причине:
     * apple-design/launching — экран точки входа должен читаться как часть
     * приложения, а не как элемент, «влетающий» поверх неё. Скругление/тень
     * уже убраны, добавлять к этому ещё и попап-scale было бы противоречием.
     */
    const initial = page && !reduceMotion
        ? {opacity: 0, y: PAGE_ENTER_OFFSET_PX}
        : fullPage || reduceMotion
        ? {opacity: 0}
        : isBottom
            ? {y: "100%"}
            : isTop || isTopRight
                ? {y: "calc(-100% - 4.5rem)", opacity: 0}
                : {scale: 0.92, opacity: 0, y: 12};
    const animate = page && !reduceMotion
        ? {opacity: 1, y: 0}
        : fullPage || reduceMotion
        ? {opacity: 1}
        : isBottom
            ? {y: 0}
            : isTop || isTopRight
                ? {y: 0, opacity: 1}
                : {scale: 1, opacity: 1, y: 0};
    const exit = initial;

    return (
        /*
         * Портал в body обязателен, а не «для красоты»: страницы рендерят часть модалок
         * сами (события, компоновка суперлайка, мэтч после свайпа), а они лежат внутри
         * <main class="relative z-10"> — это контекст наложения. Без портала такая модалка
         * с z-96 всё равно оказывалась ПОД модалками общего стека (z-80+), потому что
         * сравнивались не они, а z-10 у main. Портал поднимает все модалки на один
         * уровень, и z-index снова означает то, что написано.
         */
        <Portal>
        <div
            className={[
                "fixed inset-0 z-[80] flex touch-manipulation overflow-hidden",
                /* Плавающие панели (top/topRight — уведомления и т.п.) НЕ должны
                   глотать тапы по всему экрану: их невидимый слой inset-0 съедал
                   клики по странице под панелью (Ирина, 13.09: тап по «54%» на
                   мэтч-карточке не открывал совместимость, пока открыты
                   уведомления). Контейнер — pointer-events-none, кликабельна
                   только сама панель (см. frame ниже). Закрытие — крестиком. */
                isTop || isTopRight
                    ? "pointer-events-none"
                    : modalLayer.interactive && isPresent
                        ? "pointer-events-auto"
                        : "pointer-events-none",
                isBottom
                    ? "items-end justify-center"
                    : isTop || isTopRight
                        ? "items-start justify-center"
                        : useCardBox
                        ? "items-start justify-center"
                        : "items-center justify-center",
            ].join(" ")}
            onPointerDown={(event) => {
                pressStartedOnBackdropRef.current = event.target === event.currentTarget;
            }}
            onClick={(event) => {
                const startedOnBackdrop = pressStartedOnBackdropRef.current;
                pressStartedOnBackdropRef.current = false;
                if (!startedOnBackdrop || event.target !== event.currentTarget) return;
                if (!disableBackdropClose && onClose) onClose();
            }}
            aria-modal="true"
            role="dialog"
            aria-label={ariaLabel}
            data-presentation={page ? "page" : undefined}
            style={{
                /* Раньше top/topRight получали paddingTop без пола (только safe-area,
                   0 на устройстве без выреза) — единственный потребитель, NotificationsModal,
                   упирался скруглёнными углами карточки прямо в край экрана: не «плотный
                   заголовок», а обрезанная рамка без периметрового отступа. Остальные позиции
                   уже держат пол в 0.75rem — top/topRight теперь тоже. */
                /* fullPage: контейнер сам без паддинга — рамка (frameStyle выше)
                 * занимает 100% и несёт safe-area padding ВНУТРИ себя, поверх
                 * своего bg-white. Раньше safe-area-паддинг стоял здесь, на
                 * контейнере: у контейнера нет фона (ни общей, ни собственной
                 * подложки — см. `!fullPage` ниже), так что на устройстве с
                 * ненулевым safe-area эта полоса была прозрачной дырой до
                 * страницы под модалкой. См. комментарий у frameStyle. */
                paddingTop: fullPage ? 0 : useCardBox ? "var(--rla-card-top)" : "max(env(safe-area-inset-top), 0.75rem)",
                paddingBottom: fullPage
                    ? 0
                    : bottomInset ?? (useCardBox ? "var(--rla-card-bottom)" : isBottom ? 0 : "max(env(safe-area-inset-bottom), 0.75rem)"),
                paddingLeft: fullPage ? 0 : useCardBox ? "var(--rla-card-inset-x)" : isBottom ? 0 : "max(env(safe-area-inset-left), 0.75rem)",
                paddingRight: fullPage ? 0 : useCardBox ? "var(--rla-card-inset-x)" : isBottom ? 0 : "max(env(safe-area-inset-right), 0.75rem)",
                zIndex: zLayer ?? modalLayer.zIndex,
                /* #743 iOS WebKit: модальный слой — собственный композитный слой,
                 * иначе карточка ленты с backdrop-filter рисуется поверх (см. константу). */
                transform: COMPOSITING_LAYER_FIX,
            }}
        >
            {/*
             * Собственная подложка модалки (когда нет общего затемнения стека
             * модалок). Раньше это был статичный класс на самом контейнере —
             * тёмная мгновенно на открытии и без анимации на закрытии: рамка
             * доигрывала exit ещё 300+ мс уже без затемнения под собой, а на
             * входе фон просто «включался». Отдельный animated-слой гасит и
             * зажигает её вместе с рамкой — тот же приём (fade, ~180ms), что у
             * подложки ConfirmDialog.
             */}
            {(!hasSharedBackdrop || ownBackdrop) && !fullPage && (
                <motion.div
                    key="modal-own-backdrop"
                    initial={{opacity: 0}}
                    animate={{opacity: 1}}
                    exit={{opacity: 0}}
                    transition={{duration: reduceMotion ? 0.15 : OWN_BACKDROP_DURATION}}
                    className="pointer-events-none absolute inset-0 bg-black/50 backdrop-blur-sm"
                />
            )}
            {backdropSlot}
            <motion.div
                ref={frameRef}
                initial={initial}
                animate={animate}
                exit={exit}
                transition={reduceMotion ? MODAL_PANEL_REDUCED_MOTION_TRANSITION : page ? PAGE_TRANSITION : MODAL_PANEL_SPRING}
                className={[
                    "relative flex flex-col overflow-hidden bg-white",
                    fullPage ? "" : "shadow-2xl",
                    /* Плавающая панель кликабельна, даже если контейнер pointer-events-none
                       (см. комментарий у контейнера: top/topRight не глотают тапы вне панели). */
                    (isTop || isTopRight) ? "pointer-events-auto" : "",
                    fullPage
                        ? ""
                        : isBottom
                            ? radiusClasses.bottomSheet
                            : isTop
                                ? radiusClasses.topSheet
                                : radiusClasses.centered,
                    className,
                ].join(" ")}
                style={
                    canSwipeDismiss
                        // pan-x, а не none: горизонтальные жесты (в т.ч. системный
                        // «назад») остаются браузеру, вертикаль забирает шторка —
                        // иначе палец тянул бы страницу под шторкой, а не её саму.
                        ? {...frameStyle, touchAction: "pan-x"}
                        : frameStyle
                }
                /*
                 * Смахивание вниз. Жест ведёт motion drag, а не самописные
                 * pointer-обработчики: он читает ЖИВОЙ transform элемента, поэтому
                 * шторку можно перехватить прямо на выезде и увести обратно вниз —
                 * прерываемость по apple-design §3.
                 *
                 * dragConstraints top/bottom = 0 при dragElastic.bottom = 1 — это не
                 * «нельзя вниз», а «вниз без сопротивления, но после отпускания
                 * вернись на место»: возврат делает пружина SETTLE_SPRING (та же, что
                 * возвращает недосвайпнутую анкету). Закрытие — решение onDragEnd.
                 */
                drag={canSwipeDismiss ? "y" : false}
                dragControls={dragControls}
                dragListener={false}
                dragConstraints={{top: 0, bottom: 0}}
                dragElastic={SHEET_DRAG_ELASTIC}
                dragMomentum={false}
                dragTransition={{
                    bounceStiffness: SETTLE_SPRING.stiffness,
                    bounceDamping: SETTLE_SPRING.damping,
                }}
                onPointerDownCapture={(event) => {
                    suppressClickRef.current = false;
                    // Жест начинается в том числе с кнопок внутри шторки: тап остаётся
                    // тапом (порога смещения он не проходит), а настоящее смахивание
                    // с кнопки гасится в onClickCapture. Так же сделано в ProfileCard.
                    if (canSwipeDismiss) dragControls.start(event);
                }}
                onDragEnd={(_, info) => {
                    if (Math.abs(info.offset.y) > SHEET_CLICK_SUPPRESS_OFFSET) {
                        suppressClickRef.current = true;
                    }
                    if (!onClose) return;
                    // Решает не то, куда палец доехал, а то, куда он ЛЕТИТ:
                    // короткий бросок вниз закрывает, медленная протяжка на ту же
                    // дистанцию — нет (apple-design §6, проекция точки остановки).
                    const height = frameRef.current?.offsetHeight ?? 0;
                    const threshold = Math.min(
                        SHEET_DISMISS_MAX_PX,
                        Math.max(SHEET_DISMISS_MIN_PX, height * SHEET_DISMISS_FRACTION),
                    );
                    const projected = info.offset.y + projectEndpoint(info.velocity.y);
                    if (info.velocity.y > FLING_VELOCITY || projected > threshold) onClose();
                }}
                onClickCapture={(event) => {
                    if (!suppressClickRef.current) return;
                    suppressClickRef.current = false;
                    event.preventDefault();
                    event.stopPropagation();
                }}
                onClick={(event) => event.stopPropagation()}
            >
                {!hideCloseButton && onClose && (
                    <ModalCloseButton onClick={onClose} className={["z-20 size-9", closeButtonClassName].join(" ")}/>
                )}
                {/*
                 * Граница presence-контекста для содержимого модалки.
                 *
                 * Стек модалок закрывается через AnimatePresence в AppLayout, и любой
                 * motion-компонент внутри модалки регистрируется в том же presence.
                 * Если такой компонент смонтировать и размонтировать внутри открытой
                 * модалки (например, карточка предпоказа анкеты), внешний AnimatePresence
                 * остаётся ждать потомка, которого уже нет: выход не завершается, узел
                 * навсегда остаётся в DOM и невидимой подложкой перехватывает клики.
                 * Здесь такие компоненты регистрируются в локальном presence, а наружу
                 * «выходит» только сама рамка модалки — один стабильный motion-элемент.
                 */}
                <AnimatePresence>
                    <Fragment key="modal-content">{children}</Fragment>
                </AnimatePresence>
            </motion.div>
        </div>
        </Portal>
    );
}

export function ModalHeader({
    children,
    className = "",
    variant = "gradient",
}: {
    children: ReactNode;
    className?: string;
    variant?: "gradient" | "plain";
}) {
    const base =
        variant === "gradient"
            ? "bg-gradient-to-br from-brand-from to-brand-to text-white"
            : "bg-white text-gray-900 border-b border-gray-100";
    return (
        <div className={`flex-shrink-0 px-5 sm:px-6 py-4 sm:py-5 ${base} ${className}`}>
            {children}
        </div>
    );
}

export function ModalBody({
    children,
    className = "",
    noPadding = false,
}: {
    children: ReactNode;
    className?: string;
    noPadding?: boolean;
}) {
    return (
        <ScrollArea className={["flex-1 min-h-0", className].join(" ")}>
            <div className={noPadding ? "" : "px-5 sm:px-6 py-4 sm:py-5"}>
                {children}
            </div>
        </ScrollArea>
    );
}

export function ModalFooter({
    children,
    className = "",
}: {
    children: ReactNode;
    className?: string;
}) {
    return (
        <div
            className={[
                "flex-shrink-0 px-5 sm:px-6 py-3 sm:py-4 border-t border-gray-100 bg-white",
                className,
            ].join(" ")}
        >
            {children}
        </div>
    );
}
